import type { ReactElement } from "react";
import { Link, useLocation } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";

import { DataPending } from "./data-pending.js";
import { InvitePanel } from "./invite-panel.js";
import { tenantUserUrls } from "./urls.js";
import { UserSearchForm, type StatusChoice } from "./user-search-form.js";

/** 一覧の 1 行（サーバの ApiTenantUserRow）。 */
export interface TenantUserRow {
    readonly userName: string;
    readonly email: string;
    readonly statusLabel: string;
    readonly badgeClass: string;
    readonly canEdit: boolean;
}

/** 利用者の一覧（<c>GET /api/tenants/{契約}/users</c>。サーバの ApiTenantUserListResponse と同じ欄）。 */
export interface TenantUserList {
    readonly tenantId: number;
    readonly tenantTitle: string;
    readonly users: readonly TenantUserRow[];
    readonly matchedCount: number;
    readonly totalCount: number;
    readonly capacity: number;
    readonly isFull: boolean;
    /** 会員がプラン変更できる契約か。できない契約（無償利用・月額以外）は、上限の断りで運営へのお問い合わせを案内する。 */
    readonly canChangePlan: boolean;
    readonly canManage: boolean;
    readonly isFiltered: boolean;
    readonly emptyMessage: string;
    readonly hasMore: boolean;
    readonly remainingCount: number;
    readonly nextTake: number;
    readonly nextCount: number;
    readonly keyword: string | null;
    readonly status: number | null;
    readonly statusChoices: readonly StatusChoice[];
}

function UserRows({ list }: { readonly list: TenantUserList }): ReactElement {
    return (
        <ul className="divide-y divide-slate-200">
            {list.users.map((user) => (
                <li key={user.email} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                        <div className="truncate font-medium">{user.userName}</div>
                        <div className="truncate text-sm text-slate-500">{user.email}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                        <span className={user.badgeClass}>{user.statusLabel}</span>
                        {user.canEdit
                            ? <Link className="btn-secondary btn-sm" to={tenantUserUrls.edit(list.tenantId, user.email)}>編集</Link>
                            : null}
                    </div>
                </li>
            ))}
        </ul>
    );
}

/** 続きの読み込み。<b>行き先を持つただのリンク</b>（共有・再読み込み・戻るがそのまま効く）。 */
function MoreLink({ list }: { readonly list: TenantUserList }): ReactElement {
    return (
        <div id="more" className="mt-4 border-t border-slate-200 pt-4 text-center">
            <p className="mb-3 text-sm text-slate-500">
                {list.users.length} / {list.matchedCount} 件を表示しています
            </p>
            <Link
                className="btn-secondary"
                to={tenantUserUrls.more(list.tenantId, list.keyword, list.status, list.nextTake)}
            >
                さらに {list.nextCount} 件読み込む（残り {list.remainingCount} 件）
            </Link>
        </div>
    );
}

/** 登録ユーザの面（検索・件数・一覧・続き）。 */
function UserSection({ list }: { readonly list: TenantUserList }): ReactElement {
    return (
        <section className="card min-w-0 p-6 lg:col-span-3">
            <div className="mb-4 flex items-center justify-between">
                <h2 className="section-title mb-0">登録ユーザ</h2>
                {/* 契約人数に対する在籍数。絞り込んでも変わらない */}
                <span className="badge-muted">{list.totalCount} / {list.capacity} 名</span>
            </div>

            <UserSearchForm
                tenantId={list.tenantId} keyword={list.keyword} status={list.status}
                choices={list.statusChoices} filtered={list.isFiltered}
            />

            {list.isFiltered
                ? (
                    <p className="mb-3 text-sm text-slate-500">
                        {list.matchedCount} 件が一致しました（全 {list.totalCount} 名）
                    </p>
                )
                : null}

            {list.users.length === 0
                ? <div className="empty-state text-sm text-slate-500">{list.emptyMessage}</div>
                : (
                    <>
                        <UserRows list={list} />
                        {list.hasMore ? <MoreLink list={list} /> : null}
                    </>
                )}
        </section>
    );
}

/**
 * 利用者の一覧と招待（ユーザ管理）。
 *
 * <b>件数・続きの有無・空のときの文言・編集の入口を出すかはサーバが決める</b>（TenantUserListViewModel）。
 */
export function UserListPage({ tenantId }: { readonly tenantId: number }): ReactElement {
    usePageMeta("ユーザ管理");
    const { search } = useLocation();
    const data = useApiData<TenantUserList>(tenantUserUrls.api.list(tenantId, search));

    if (data.state !== "ready") {
        return <DataPending data={data} width="max-w-6xl" />;
    }

    const list = data.value;
    return (
        <div className="page max-w-6xl">
            <p className="mb-4"><Link to={tenantUserUrls.tenants} className="btn-secondary btn-sm">契約一覧へ戻る</Link></p>
            <div className="page-header">
                <div>
                    <h1 className="page-title">ユーザ管理</h1>
                    <p className="page-subtitle">{list.tenantTitle}</p>
                </div>
            </div>

            {/* 枠には min-w-0 を付ける。長いメールアドレスが 1 つ入るだけでページごと横に広がる */}
            <div className="grid gap-4 lg:grid-cols-5">
                <UserSection list={list} />
                {list.canManage ? <InvitePanel tenantId={list.tenantId} full={list.isFull} canChangePlan={list.canChangePlan} /> : null}
            </div>
        </div>
    );
}
